import clsx from 'clsx';
import { Field, Form, Formik } from 'formik';
import React, { useEffect, useRef } from 'react';

import { TextButton } from '@components/Button/variants';
import SearchIcon from '@components/Images/SearchIcon';
import useEventTracking from '@hooks/useEventTracking';
import { graphics_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { cleanQueryForClickText } from '@utils/tracking';

import { SEARCH_INPUT_FIELD_NAME } from '../constants';
import type { GraphicsSearchPageSearchBarProps } from '../interfaces';
import PopularTopics from './PopularTopics';

const GraphicsSearchPageSearchBar = ({
  isDisabled = false,
  isCompact = false,
  searchQuery,
}: GraphicsSearchPageSearchBarProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const setFieldValueRef = useRef<((field: string, value: string) => void) | null>(null);
  const previousSearchQueryRef = useRef<string | undefined>(searchQuery);

  const { trackEvent } = useEventTracking({});

  useEffect(() => {
    if (
      setFieldValueRef.current &&
      searchQuery !== undefined &&
      searchQuery !== previousSearchQueryRef.current
    ) {
      setFieldValueRef.current(SEARCH_INPUT_FIELD_NAME, searchQuery);
      previousSearchQueryRef.current = searchQuery;
    }
  }, [searchQuery]);

  const handleSubmit = (values: { query: string }) => {
    const basePath = graphics_path();
    const cleanedQuery = cleanQueryForClickText(values.query);
    const searchParams = new URLSearchParams({
      query: cleanedQuery,
    });

    const newLink = `${basePath}?${searchParams.toString()}`;

    trackEvent(
      click({
        clickTarget: ClickTarget.GraphicsSearchPageSearchSubmit,
        clickText: cleanedQuery,
        clickLink: newLink,
      })
    );

    const anchor = document.createElement('a');
    anchor.href = newLink;
    anchor.click();
    anchor.remove();
  };

  return (
    <div
      className={clsx('flex w-full flex-col flex-nowrap items-start', {
        'sm:flex-row sm:items-end sm:justify-between sm:gap-56': !isCompact,
      })}
    >
      <Formik
        initialValues={{
          query: searchQuery || '',
        }}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          setFieldValueRef.current = setFieldValue;

          return (
            <Form ref={formRef} id="form_id" data-id="search-form">
              <div className="relative w-full max-w-[260px]">
                <Field
                  innerRef={inputRef}
                  autoFocus={!isCompact}
                  className="text-grayscale-black h-40 w-full rounded-none border-0 border-b border-grayscale-charcoal bg-transparent py-2 pl-0 pr-80 font-suisse-screen text-headline-alt-sm placeholder:text-grayscale-gray/50 focus:outline-none"
                  disabled={isDisabled}
                  name={SEARCH_INPUT_FIELD_NAME}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    setFieldValue(SEARCH_INPUT_FIELD_NAME, newValue);
                  }}
                  placeholder="Data Search"
                  type="text"
                />
                {values.query && (
                  <TextButton
                    as="button"
                    type="button"
                    onClick={() => {
                      setFieldValue(SEARCH_INPUT_FIELD_NAME, '');
                      inputRef.current?.focus();
                    }}
                    className="!absolute right-32 top-1/2 !w-fit -translate-y-1/2 p-0 font-suisse-intl-mono text-overline-sm font-normal !text-grayscale-gray !no-underline"
                    aria-label="Clear search"
                  >
                    clear
                  </TextButton>
                )}
                <TextButton
                  as="button"
                  aria-label="Search for relevant visual storytelling, charts, maps, etc."
                  className="!absolute right-0 top-0 !h-40 !w-40"
                  type="submit"
                  size="small"
                >
                  <SearchIcon
                    className="absolute right-2 top-1/2 h-20 w-20 -translate-y-1/2"
                    aria-label="Search for your query"
                  />
                </TextButton>
              </div>
            </Form>
          );
        }}
      </Formik>
      <PopularTopics isCompact={isCompact} />
    </div>
  );
};

export default GraphicsSearchPageSearchBar;
